import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface CardHolder {
  id: string;
  holderName: string;
  email: string | null;
  phone: string;
  aadharNumber?: string | null;
  city?: string | null;
  block?: string | null;
  district?: string | null;
  state?: string | null;
  address?: string | null;
  isActive: boolean;
  isPrimary?: boolean;
  isBlocked?: boolean;
  branchId?: string;
  branch?: {
    id: string;
    name: string;
  };
  nfcCards?: Array<{
    id: string;
    displayNumber: string;
    balance: number;
    status: string;
    isAssigned: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface NFCCardScanResult {
  card: {
    id: string;
    displayNumber: string;
    balance: number;
    status: string;
    isAssigned: boolean;
    isBlocked?: boolean;
    blockedReason?: string | null;
  };
  holder: CardHolder | null;
  linkedCards?: Array<{
    id: string;
    displayNumber: string;
    balance: number;
    status: string;
  }>;
  totalCards?: number;
}

export interface CardByNumberResult {
  card: {
    id: string;
    displayNumber: string;
    balance: number;
    status: string;
    holderId: string | null;
  };
  holder: CardHolder;
}

export interface CreateCardHolderData {
  holderName: string;
  email?: string;
  phone: string;
  aadharNumber: string;
  city: string;
  block: string;
  district: string;
  state: string;
  address: string;
  branchId?: string;
  isPrimary?: boolean;
}

export interface UpdateCardHolderData {
  holderName?: string;
  email?: string;
  phone?: string;
  aadharNumber?: string;
  city?: string;
  block?: string;
  district?: string;
  state?: string;
  address?: string;
  isActive?: boolean;
  branchId?: string;
}

export type CardHolderSortField =
  | "holderName"
  | "aadharNumber"
  | "phone"
  | "city"
  | "district"
  | "state"
  | "branch"
  | "status"
  | "createdAt";

export type CardHolderSortOrder = "ASC" | "DESC";

export interface CardHolderFilter {
  pageNo?: number;
  pageSize?: number;
  search?: string;
  holderName?: string;
  email?: string;
  branchId?: string;
  isActive?: boolean;
  sortBy?: CardHolderSortField;
  sortOrder?: CardHolderSortOrder;
}

export const cardHolderAPI = {
  getCardHolders: async (filters?: CardHolderFilter) => {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const response = await api.get(`/cardholders?${params.toString()}`);
    const { data, total } = parsePaginatedList<CardHolder>(response.data.data);
    return { items: data, total };
  },

  getCardHolderById: async (id: string): Promise<CardHolder> => {
    const response = await api.get(`/cardholders/${id}`);
    return response.data.data;
  },

  scanCard: async (cardNumber: string): Promise<NFCCardScanResult> => {
    const response = await api.get(`/cardholders/scan/${cardNumber}`);
    return response.data.data;
  },

  getCardHolderByCardNumber: async (
    cardNumber: string
  ): Promise<CardByNumberResult> => {
    const response = await api.get(`/cardholders/card/${cardNumber}`);
    return response.data.data;
  },

  getCardsByPhone: async (phone: string) => {
    const response = await api.get(`/cardholders/by-phone/${phone}`);
    return response.data.data || [];
  },

  createCardHolder: async (data: CreateCardHolderData): Promise<CardHolder> => {
    const response = await api.post("/cardholders", data);
    return response.data.data;
  },

  updateCardHolder: async (
    id: string,
    data: UpdateCardHolderData
  ): Promise<CardHolder> => {
    const response = await api.put(`/cardholders/${id}`, data);
    return response.data.data;
  },

  deleteCardHolder: async (id: string) => {
    const response = await api.delete(`/cardholders/${id}`);
    return response.data;
  },

  transferBalance: async (
    fromCardId: string,
    toCardId: string,
    amount: number
  ) => {
    const response = await api.post("/cardholders/transfer-balance", {
      fromCardId,
      toCardId,
      amount,
    });
    return response.data.data;
  },

  getCardHolderStats: async () => {
    const response = await api.get("/cardholders/stats");
    return response.data.data;
  },
};
